import { useState, useEffect, useCallback, useReducer, useRef, useMemo } from 'react';
import services from '../services';
import { processQuestionsForExam } from '../utils/questionUtils';
import { convertDifficultyStringsToIds } from '../utils/difficultyMapping';

/**
 * Custom hook for managing question state
 * @returns {Object} Question state and methods
 */
export const useQuestionState = (initialIndex = 0) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(initialIndex);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isAnswerChecked, setIsAnswerChecked] = useState(false);
  const [isCorrect, setIsCorrect] = useState(null);
  const [isFlagged, setIsFlagged] = useState(false);
  const [showClues, setShowClues] = useState(false);
  const [questionStatuses, setQuestionStatuses] = useState([]);
  const [questionHistory, setQuestionHistory] = useState([]);
  
  // Reset the question state when navigating to a new question
  const resetQuestionState = useCallback(() => {
    setSelectedAnswer(null);
    setIsAnswerChecked(false);
    setIsCorrect(null);
    setIsFlagged(false);
    setShowClues(false);
  }, []);
  
  // Add the current question to history
  const addToQuestionHistory = useCallback((index) => {
    setQuestionHistory(prev => [...prev, index]);
  }, []);
  
  // Toggle the flagged state
  const toggleFlagged = useCallback(() => {
    setIsFlagged(prev => !prev);
  }, []);
  
  // Toggle the clues display
  const toggleClues = useCallback(() => {
    setShowClues(prev => !prev);
  }, []);
  
  return {
    currentQuestionIndex,
    setCurrentQuestionIndex,
    selectedAnswer,
    setSelectedAnswer,
    isAnswerChecked,
    setIsAnswerChecked,
    isCorrect,
    setIsCorrect,
    isFlagged,
    setIsFlagged,
    showClues,
    setShowClues,
    questionStatuses,
    setQuestionStatuses,
    questionHistory,
    setQuestionHistory,
    resetQuestionState,
    addToQuestionHistory,
    toggleFlagged,
    toggleClues
  };
};

/**
 * Custom hook for managing session setup
 * @param {Object} initialSetup - Initial setup values
 * @param {Array} systemOptions - Array of system category options (with id and name)
 * @param {Array} disciplineOptions - Array of discipline category options (with id and name)
 * @param {Array} userFolderOptions - Array of user folder options (with id and name)
 * @returns {Object} Session setup state and methods
 */
export const useSessionSetup = (initialSetup = {}, systemOptions = [], disciplineOptions = [], userFolderOptions = []) => {
  const defaultSetup = {
    title: "",
    examType: "",
    examTypeId: "",
    system: ["ALL"],
    discipline: ["ALL"],
    difficulty: ["ALL"],
    questionCount: "50",
    filterOption: "None",
    userFolders: ["None"] // NEW default for user folders
  };
  
  const [sessionSetup, setSessionSetup] = useState({ ...defaultSetup, ...initialSetup });
  const [setupComplete, setSetupComplete] = useState(false);
  const [availableQuestionsCount, setAvailableQuestionsCount] = useState(0);
  const [isLoadingQuestionCount, setIsLoadingQuestionCount] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [isSessionEnding, setIsSessionEnding] = useState(false);
  
  // Validation states
  const [titleError, setTitleError] = useState("");
  const [questionCountError, setQuestionCountError] = useState("");
  
  // Reset session state
  const resetSessionState = useCallback(() => {
    setSessionSetup({ ...defaultSetup });
    setSetupComplete(false);
    setCurrentSessionId(null);
    setIsSessionEnding(false);
    setTitleError("");
    setQuestionCountError("");  }, [defaultSetup]);

  // Cache for question count requests to avoid duplicate API calls
  const questionCountCache = useRef(new Map());
  const activeRequest = useRef(null);

  // Fetch question count based on current filters
  const fetchQuestionCount = useCallback(async () => {
    if (!sessionSetup.examTypeId) {
      setAvailableQuestionsCount(0);
      return;
    }

    // Create a cache key based on the request parameters
    const cacheKey = JSON.stringify({
      examTypeId: sessionSetup.examTypeId,
      system: sessionSetup.system,
      discipline: sessionSetup.discipline,
      difficulty: sessionSetup.difficulty,
      filterOption: sessionSetup.filterOption,
      userFolders: sessionSetup.userFolders // include userFolders (IDs) in cache key
    });

    // Check if we have a cached result that's less than 30 seconds old
    const cached = questionCountCache.current.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp) < 30000) {
      setAvailableQuestionsCount(cached.count);
      return;
    }

    // If there's already an active request with the same parameters, don't make another
    if (activeRequest.current && activeRequest.current.key === cacheKey) {
      return activeRequest.current.promise;
    }

    try {
      setIsLoadingQuestionCount(true);
      // console.log('[useSessionSetup] systemOptions:', systemOptions);
      // console.log('[useSessionSetup] disciplineOptions:', disciplineOptions);
      // console.log('[useSessionSetup] selected systems:', sessionSetup.system);
      // console.log('[useSessionSetup] selected disciplines:', sessionSetup.discipline);
      // Extract systemIds - handle both object format and string format
      let selectedSystemIds = [];
      if (sessionSetup.system && sessionSetup.system.length > 0) {
        if (sessionSetup.system.includes('ALL') || sessionSetup.system.some(s => typeof s === 'object' && s.name === 'ALL')) {
          selectedSystemIds = [];
        } else {
          selectedSystemIds = sessionSetup.system.map(item => {
            if (typeof item === 'object' && item.id) {
              return item.id; // Use the ID directly from the object
            } else if (typeof item === 'string') {
              // Try to find the ID from the systemOptions
              const systemOption = systemOptions.find(opt => opt.name === item);
              return systemOption ? systemOption.id : null;
            }
            return null;
          }).filter(Boolean);
        }
      }
      
      // Extract disciplineIds - handle both object format and string format
      let selectedDisciplineIds = [];
      if (sessionSetup.discipline && sessionSetup.discipline.length > 0) {
        if (sessionSetup.discipline.includes('ALL') || sessionSetup.discipline.some(d => typeof d === 'object' && d.name === 'ALL')) {
          selectedDisciplineIds = [];
        } else {
          selectedDisciplineIds = sessionSetup.discipline.map(item => {
            if (typeof item === 'object' && item.id) {
              return item.id; // Use the ID directly from the object
            } else if (typeof item === 'string') {
              // Try to find the ID from the disciplineOptions
              const disciplineOption = disciplineOptions.find(opt => opt.name === item);
              return disciplineOption ? disciplineOption.id : null;
            }
            return null;
          }).filter(Boolean);
        }
      }
      // console.log('[useSessionSetup] selectedSystemIds:', selectedSystemIds);
      // console.log('[useSessionSetup] selectedDisciplineIds:', selectedDisciplineIds);
      const categoryIds = [...selectedSystemIds, ...selectedDisciplineIds];
      // console.log('[useSessionSetup] categoryIds for question count:', categoryIds);
      const filterRequest = {
        examTypeId: sessionSetup.examTypeId,
        categoryIds,
        difficulty: []
      };
      if (sessionSetup.difficulty && !sessionSetup.difficulty.includes('ALL')) {
        // console.log('[useSessionSetup] Converting difficulty strings:', sessionSetup.difficulty);
        const convertedDifficulty = convertDifficultyStringsToIds(sessionSetup.difficulty);
        // console.log('[useSessionSetup] Converted difficulty IDs:', convertedDifficulty);
        filterRequest.difficulty = convertedDifficulty;
      }
      // Map filterOption to API param
      switch (sessionSetup.filterOption) {
        case 'Questions I have never seen':
          filterRequest.neverSeen = true;
          break;
        case 'Questions I have answered incorrectly':
          filterRequest.answeredIncorrectly = true;
          break;
        case 'Questions I have answered correctly':
          filterRequest.answeredCorrectly = true;
          break;
        case 'Questions I have seen before':
          filterRequest.seenBefore = true;
          break;
        case 'None':
        default:
          // No filter
          break;
      }
      // Directly use userFolders (IDs) for userFolderIds
      if (
        sessionSetup.userFolders &&
        sessionSetup.userFolders.length > 0 &&
        !sessionSetup.userFolders.includes(null) &&
        !sessionSetup.userFolders.includes('None')
      ) {
        filterRequest.userFolderIds = sessionSetup.userFolders;
      }
      // console.log('[useSessionSetup] Final filter request being sent to API:', JSON.stringify(filterRequest, null, 2));
      
      // Store the active request to prevent duplicates
      const requestPromise = services.exam.getQuestionsCount(filterRequest);
      activeRequest.current = { key: cacheKey, promise: requestPromise };
      
      const count = await requestPromise;
      
      // Cache the result
      questionCountCache.current.set(cacheKey, {
        count,
        timestamp: Date.now()
      });
      
      setAvailableQuestionsCount(count);
      const currentCount = parseInt(sessionSetup.questionCount);
      if (!isNaN(currentCount) && currentCount > count && count > 0) {
        setSessionSetup(prev => ({
          ...prev,
          questionCount: count.toString()
        }));
      }
    } catch (error) {
      console.error('Error fetching question count:', error);
      setAvailableQuestionsCount(0);
    } finally {
      setIsLoadingQuestionCount(false);
      // Clear the active request
      if (activeRequest.current && activeRequest.current.key === cacheKey) {
        activeRequest.current = null;
      }
    }
  }, [sessionSetup, systemOptions, disciplineOptions, userFolderOptions]);
  
  return {
    sessionSetup,
    setSessionSetup,
    setupComplete,
    setSetupComplete,
    availableQuestionsCount,
    setAvailableQuestionsCount,
    isLoadingQuestionCount,
    currentSessionId,
    setCurrentSessionId,
    isSessionEnding,
    setIsSessionEnding,
    titleError,
    setTitleError,
    questionCountError,
    setQuestionCountError,
    resetSessionState,
    fetchQuestionCount
  };
};

/**
 * Custom hook for managing questions data
 * @returns {Object} Questions state and methods
 */
export const useQuestionsData = () => {
  const [questions, setQuestions] = useState([]);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [currentQuestionNumber, setCurrentQuestionNumber] = useState(1);
  const [progressPercentage, setProgressPercentage] = useState(0);
  
  // 🛡️ ENHANCED DUPLICATE PREVENTION: Track in-flight requests to prevent race conditions
  const [inFlightRequests, setInFlightRequests] = useState(new Set());
  // DEBUG: Add logging for state changes
  useEffect(() => {
    // console.log('[useQuestionsData] ===== HOOK STATE UPDATED =====');
    // console.log('[useQuestionsData] questions state updated:', {
    //   questionsLength: questions.length,
    //   totalQuestions,
    //   loading,
    //   error,
    //   timestamp: new Date().toISOString(),
    //   questionsPreview: questions.length > 0 ? `First question: ${questions[0]?.questionText?.substring(0, 50)}...` : 'No questions'
    // });
    // console.log('[useQuestionsData] =====================================');
  }, [questions, totalQuestions, loading, error]);
  // Fetch questions for session using HATEOAS links
  const fetchQuestionsForSession = useCallback(async (sessionId, mockData) => {
    // console.log(`🚨 [useQuestionsData] ═══ FETCH QUESTIONS FOR SESSION CALLED ═══`);
    // console.log(`🚨 [useQuestionsData] 🆔 Session ID: ${sessionId}`);
    // console.log(`🚨 [useQuestionsData] 📋 Mock data length: ${mockData?.length || 0}`);
    // console.log(`🚨 [useQuestionsData] 🕐 Fetch timestamp: ${new Date().toISOString()}`);
    
    if (!sessionId) {
      console.warn('🚨 [useQuestionsData] ❌ No sessionId provided to fetchQuestionsForSession');
      return;
    }
    
    // 🛡️ IMMEDIATE DUPLICATE CHECK: Block before ANY processing
    if (inFlightRequests.has(sessionId)) {
      // console.log(`🚨 [useQuestionsData] 🛡️ DUPLICATE BLOCKED: Request in-flight for session ${sessionId}`);
      return { questions, responses, totalQuestions };
    }
    
    // 🛡️ CONTENT CHECK: Already have data for this session?
    const hasQuestionsForSession = questions.length > 0 && !loading;
    if (hasQuestionsForSession) {
      // console.log(`🚨 [useQuestionsData] 🛡️ DUPLICATE BLOCKED: Already have ${questions.length} questions`);
      return { questions, responses, totalQuestions };
    }
    
    // console.log(`🚨 [useQuestionsData] 📍 Stack trace:`);
    // console.trace(`🚨 [useQuestionsData] fetchQuestionsForSession origin`);
    
    // 🛡️ MARK REQUEST AS IN-FLIGHT BEFORE STARTING
    // console.log(`🚨 [useQuestionsData] 🚀 Starting fetch for session ${sessionId}`);
    setInFlightRequests(prev => new Set(prev).add(sessionId));
    
    try {
      setLoading(true);
      setError(null);
      
      // console.log(`[fetchQuestionsForSession] Starting HATEOAS-based fetch for session ${sessionId}`);
      
      // First, get the exam session to obtain HATEOAS links
      const examSession = await services.exam.getExamSessionById(sessionId);
      // console.log('Exam session fetched:', examSession);
      
      if (!examSession || !examSession._links) {
        console.warn('No HATEOAS links found in exam session, falling back to manual parsing');
        // Fallback to the old manual parsing method if no HATEOAS links
        return await fallbackToManualParsing(examSession, sessionId, mockData);
      }
      
      // Extract the questions and responses links from the exam session
      const questionsLink = examSession._links.questions?.href;
      const responsesLink = examSession._links.responses?.href;
      
      // console.log('HATEOAS links found:', {
      //   questionsLink,
      //   responsesLink,
      //   allLinks: Object.keys(examSession._links)
      // });
      
      let questionsData = [];
      let responsesData = [];
      
      // Fetch questions using HATEOAS link
      if (questionsLink) {
        // console.log(`[fetchQuestionsForSession] Fetching questions via HATEOAS link: ${questionsLink}`);
        questionsData = await services.exam.getExamQuestionsByLink(questionsLink);
        // console.log(`[fetchQuestionsForSession] Successfully fetched ${questionsData.length} questions via HATEOAS`);
      } else {
        console.warn('No questions link found in exam session, using fallback method');
        // Fallback to manual parsing if no questions link
        questionsData = await extractQuestionsFromSession(examSession, sessionId, mockData);
      }
      
      // Fetch responses using HATEOAS link (for resume functionality)
      if (responsesLink) {
        // console.log(`[fetchQuestionsForSession] Fetching responses via HATEOAS link: ${responsesLink}`);
        try {
          responsesData = await services.exam.getExamResponsesByLink(responsesLink);
          // console.log(`[fetchQuestionsForSession] Successfully fetched ${responsesData.length} responses via HATEOAS`);
        } catch (responsesError) {
          console.warn('Failed to fetch responses via HATEOAS, this is normal for new exam sessions:', responsesError.message);
          responsesData = [];
        }
      } else {
        // console.log('No responses link found, initializing with empty responses (normal for new sessions)');
        responsesData = [];      }        // Store the fetched data
      // console.log(`[fetchQuestionsForSession] About to update hook state with ${questionsData.length} questions`);
      // console.log(`[fetchQuestionsForSession] Current hook state before update:`, {
      //   questionsLength: questions.length,
      //   totalQuestions,
      //   loading,
      //   error
      // });
      
      // 🎯 FIX: Process questions to ensure they have examId before setting in state
      const examId = examSession.id || sessionId;
      const processedQuestions = processQuestionsForExam(questionsData, examId);
      // console.log(`[fetchQuestionsForSession] Processed ${processedQuestions.length} questions with examId: ${examId}`);
        setQuestions(processedQuestions);
      setTotalQuestions(processedQuestions.length);
      setResponses(responsesData);
      
      // console.log(`🚨 [useQuestionsData] ✅ Hook state setters called - questions: ${processedQuestions.length}, totalQuestions: ${processedQuestions.length}, responses: ${responsesData.length}`);
      // console.log(`🚨 [useQuestionsData] ✅ HATEOAS fetch completed: ${processedQuestions.length} questions, ${responsesData.length} responses`);
      // console.log(`🚨 [useQuestionsData] 🛡️ Recorded successful fetch for session: ${sessionId}`);
      
      // 🛡️ CLEAR IN-FLIGHT REQUEST - MARK AS COMPLETED
      setInFlightRequests(prev => {
        const newSet = new Set(prev);
        newSet.delete(sessionId);
        return newSet;
      });
      
      return {
        questions: processedQuestions,
        responses: responsesData,
        examSession: examSession
      };
      
    } catch (err) {
      console.error('Error in HATEOAS-based fetchQuestionsForSession:', err);
      setError('Failed to load questions. Please try again later.');
        // Fall back to mock data if everything fails
      if (mockData) {
        // console.log('Using mock data as final fallback');
        setQuestions(mockData);
        setTotalQuestions(mockData.length);
        setResponses([]);
        return {
          questions: mockData,
          responses: [],
          examSession: null
        };
      }
      
      setResponses([]);
      return {
        questions: [],
        responses: [],
        examSession: null
      };
    } finally {
      setLoading(false);
      // 🛡️ ALWAYS CLEAR IN-FLIGHT REQUEST - EVEN ON ERROR
      setInFlightRequests(prev => {
        const newSet = new Set(prev);
        newSet.delete(sessionId);
        return newSet;
      });
    }
  }, [questions, loading, responses, totalQuestions, inFlightRequests]);
  
  // Helper function for fallback to manual parsing when HATEOAS links are not available
  const fallbackToManualParsing = async (examDetails, sessionId, mockData) => {
    // console.log('[fallbackToManualParsing] Attempting manual parsing of exam session response');
    
    // Parse the response to extract questions (different formats possible)
    let questionsData = [];
    let foundQuestions = false;
    
    // Check if response has property with name matching session ID
    const sessionIdKey = sessionId.toString();
    if (examDetails && examDetails[sessionIdKey] && 
        examDetails[sessionIdKey].examQuestions && 
        Array.isArray(examDetails[sessionIdKey].examQuestions)) {
      questionsData = examDetails[sessionIdKey].examQuestions;
      foundQuestions = true;
      // console.log(`[fallbackToManualParsing] Found ${questionsData.length} questions in examDetails[${sessionIdKey}].examQuestions`);
    } else if (examDetails && examDetails.examQuestions && 
              Array.isArray(examDetails.examQuestions)) {
      questionsData = examDetails.examQuestions;
      foundQuestions = true;
      // console.log(`[fallbackToManualParsing] Found ${questionsData.length} questions in examDetails.examQuestions`);
    } else if (examDetails && Array.isArray(examDetails)) {
      // Some API responses return the questions directly as an array
      questionsData = examDetails;
      foundQuestions = true;
      // console.log(`[fallbackToManualParsing] Found ${questionsData.length} questions in examDetails array`);
    } else {
      // Look through all properties to find an array of exam questions
      for (const key in examDetails) {
        // Direct array of questions on a property
        if (examDetails[key] && Array.isArray(examDetails[key])) {
          questionsData = examDetails[key];
          foundQuestions = true;
          // console.log(`[fallbackToManualParsing] Found ${questionsData.length} questions in examDetails["${key}"] array`);
          break;
        }
        
        // Nested examQuestions property
        if (examDetails[key] && 
            examDetails[key].examQuestions && 
            Array.isArray(examDetails[key].examQuestions)) {
          questionsData = examDetails[key].examQuestions;
          foundQuestions = true;
          // console.log(`[fallbackToManualParsing] Found ${questionsData.length} questions in examDetails["${key}"].examQuestions`);
          break;
        }
        
        // Nested questions property
        if (examDetails[key] && 
            examDetails[key].questions && 
            Array.isArray(examDetails[key].questions)) {
          questionsData = examDetails[key].questions;
          foundQuestions = true;
          // console.log(`[fallbackToManualParsing] Found ${questionsData.length} questions in examDetails["${key}"].questions`);
          break;
        }
      }
    }
      if (!foundQuestions) {
      console.warn('[fallbackToManualParsing] Failed to find questions in any expected format, using mock data');
      console.warn('[fallbackToManualParsing] API response structure:', JSON.stringify(examDetails, null, 2));
      questionsData = mockData || [];
    }
    
    // Process questions to ensure they have examId and HATEOAS links
    const examId = examDetails?.id || sessionId;
    const processedQuestions = processQuestionsForExam(questionsData, examId);
    // console.log(`[fallbackToManualParsing] Processed ${processedQuestions.length} questions with examId: ${examId}`);
    
    return {
      questions: processedQuestions,
      responses: [],
      examSession: examDetails
    };
  };
  
  // Helper function to extract questions from session when no HATEOAS links are present
  const extractQuestionsFromSession = async (examSession, sessionId, mockData) => {
    // console.log('[extractQuestionsFromSession] Extracting questions without HATEOAS links');
    const result = await fallbackToManualParsing(examSession, sessionId, mockData);
    return result.questions;
  };
  // 🚀 PERFORMANCE: Memoize the return object to prevent unnecessary re-renders
  const questionsDataValue = useMemo(() => ({
    questions,
    setQuestions,
    responses,
    setResponses,
    loading,
    setLoading,
    error,
    setError,
    totalQuestions,
    setTotalQuestions,
    currentQuestionNumber,
    setCurrentQuestionNumber,
    progressPercentage,
    setProgressPercentage,
    fetchQuestionsForSession
  }), [
    questions,
    responses,
    loading,
    error,
    totalQuestions,
    currentQuestionNumber,
    progressPercentage,
    fetchQuestionsForSession
  ]);
  
  return questionsDataValue;
};

/**
 * Custom hook for managing time tracking
 * @returns {Object} Time tracking state and methods
 */
export const useTimeTracker = () => {
  const [timeTracker, setTimeTracker] = useState({
    questionStartTime: Date.now(),
    totalTimeSpent: 0
  });
  
  // Reset the timer for a new question
  const resetQuestionTimer = useCallback(() => {
    setTimeTracker(prev => ({
      ...prev,
      questionStartTime: Date.now()
    }));
  }, []);
  
  // Get the time spent on the current question in seconds
  const getCurrentQuestionTimeSpent = useCallback(() => {
    const now = Date.now();
    return Math.floor((now - timeTracker.questionStartTime) / 1000);
  }, [timeTracker.questionStartTime]);
  
  // Update the total time spent
  const updateTotalTimeSpent = useCallback(() => {
    const currentTimeSpent = getCurrentQuestionTimeSpent();
    setTimeTracker(prev => ({
      questionStartTime: Date.now(), // Reset for next question
      totalTimeSpent: prev.totalTimeSpent + currentTimeSpent
    }));
    return currentTimeSpent;
  }, [getCurrentQuestionTimeSpent]);
  
  return {
    timeTracker,
    setTimeTracker,
    resetQuestionTimer,
    getCurrentQuestionTimeSpent,
    updateTotalTimeSpent
  };
};

// Helper to flatten options tree to a name->id map, skipping 'ALL' node
function flattenOptions(options) {
  const map = {};
  function recurse(arr) {
    arr.forEach(opt => {
      if (opt.name && opt.id && opt.name !== 'ALL') map[opt.name] = opt.id;
      if (opt.children && opt.children.length > 0) recurse(opt.children);
    });
  }
  recurse(options);
  return map;
}
